import { useEffect, useState } from 'react';
import './style.scss';
import { TranslationService } from '../../../../services/translation.service';
import { type ITravelInformationState } from '../TravelInformation';
import { type ICarInformationState } from '../CarInformation';
import { PublicTransportCalculator } from '../../helpers/public-transport.calculator';
import { VariableCarCostsCalculator } from '../../helpers/variable-car-costs.calculator';
import { TotalCarCostsCalculator } from '../../helpers/total-car-cost.calculator';
import { PublicTransportConstants } from '../../constants/public-transport.constants';
import ResultTextHelper from '../../helpers/result-text-helper';
import { SanitizerService } from '../../../../services/sanitizer.service';
import { TicketHelper } from '../../helpers/ticket.helper';
import { AnalyticsService } from '../../../../services/analytics-service';

function ResultWithGraph({
  travelInformation,
  carInformation,
  updateResult,
  resetUpdateResult,
  usedDistanceInCalculationValueChanged,
}: {
  travelInformation: ITravelInformationState;
  carInformation: ICarInformationState;
  updateResult: boolean;
  resetUpdateResult: () => void;
  usedDistanceInCalculationValueChanged?: (value: number) => void;
}) {
  // === STATE ===
  const [totalCostPublicTransportState, setTotalCostPublicTransportState] = useState(0);
  const [totalCarCostState, setTotalCarCostState] = useState(0);
  const [variableCarCostState, setVariableCarCostState] = useState(0);
  const [cheapestAlternativeText, setCheapestAlternativeText] = useState('');
  const [cheapestHeadingText, setCheapestHeadingText] = useState('');

  // === VARIABLES ===
  const maxValue = Math.max(totalCostPublicTransportState, variableCarCostState, totalCarCostState, 1);
  const barHeight = (value: number) => `${(value / maxValue) * 100}%`;

  // === EFFECTS ===
  useEffect(() => {
    function getCheapestHeadingText(publicTransportCost: number, carCost: number) {
      if (publicTransportCost <= carCost)
        return TranslationService.get('pricecalculatorpage.commutebypublictransportischeapestheadingtext').replace('{cheapest-alternative-cost}', `${(carCost - publicTransportCost).toString()}`);
      return TranslationService.get('pricecalculatorpage.commutebycarischeapestheadingtext').replace('{cheapest-alternative-cost}', `${(publicTransportCost - carCost).toString()}`);
    }

    function getCheapestAlternativeText(publicTransportCost: number, carCost: number): string {
      if (publicTransportCost <= carCost) {
        const { selectedTicketCost } = ResultTextHelper.getSelectedTicketAndCost(travelInformation.value.prefferredTicketType, travelInformation.value.travelZones);
        return TranslationService.get('pricecalculatorpage.commutebypublictransportischeapestalternativetext')
          .replace(
            '{selected-ticket}',
            TicketHelper.getSelectedTicketWithNumberOfZonesText(travelInformation.value.prefferredTicketType, travelInformation.value.travelZones, (x) => TranslationService.get(x)),
          )
          .replace('{selected-ticket-cost}', `${selectedTicketCost}`);
      }
      return TranslationService.get('pricecalculatorpage.commutebycarischeapestalternativetext').replace('{cheapest-alternative-cost}', `${(publicTransportCost - carCost).toString()}`);
    }

    function updateResultCalculations() {
      const publicTransportCost = PublicTransportCalculator.calculateCost(
        {
          numberOfTravelDaysPerMonth: travelInformation.value.travelDaysPerMonth || 0,
          zonesToTravelWithin: travelInformation.value.travelZones,
          preferredTicketType: travelInformation.value.prefferredTicketType,
        },
        PublicTransportConstants,
      );
      setTotalCostPublicTransportState(publicTransportCost.costInSek);

      const variableCarCost = VariableCarCostsCalculator.calculateCost(carInformation.value, travelInformation.value);
      setVariableCarCostState(variableCarCost.costPerMonth);

      const totalCarCost = TotalCarCostsCalculator.calculateCost(carInformation.value, travelInformation.value);
      setTotalCarCostState(totalCarCost.totalCost);

      if (totalCarCost.yearlyCommuteGreatherThanTotalDistance && usedDistanceInCalculationValueChanged) usedDistanceInCalculationValueChanged(totalCarCost.yearlyCommuteKm);

      setCheapestHeadingText(getCheapestHeadingText(publicTransportCost.costInSek, variableCarCost.costPerMonth));
      setCheapestAlternativeText(getCheapestAlternativeText(publicTransportCost.costInSek, variableCarCost.costPerMonth));

      let trackingLabel: string;
      if (Math.round(publicTransportCost.costInSek) === Math.round(variableCarCost.costPerMonth)) trackingLabel = 'Samma';
      else if (publicTransportCost.costInSek <= variableCarCost.costPerMonth) trackingLabel = 'Kollektivtrafik';
      else trackingLabel = 'Bilpendling';
      AnalyticsService.addEvent('PriceCalculator', 'Resultat uppdaterat', trackingLabel);
    }

    if (updateResult) {
      updateResultCalculations();
      resetUpdateResult();
    }
  }, [updateResult, resetUpdateResult, travelInformation, carInformation, usedDistanceInCalculationValueChanged]);

  // === HANDLERS ===
  function createAriaLiveText(publicTransportCost: number, variableCarCost: number, totalCarCost: number): string {
    return `${TranslationService.get('pricecalculatorpage.resultheading')} ${TranslationService.get('pricecalculatorpage.result.ariagraphdescription')
      .replace('#publictransport#', publicTransportCost.toString())
      .replace('#carcommutingcost#', variableCarCost.toString())
      .replace('#totalcarcost#', totalCarCost.toString())}`;
  }

  return (
    <div className="result-with-graph">
      <div className="container mb-4 mb-md-5">
        <div className="container container--narrower text-center" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(cheapestHeadingText) }}></div>
        <div className="result-with-graph__chart d-flex align-items-stretch justify-content-center mb-1 border-bottom" aria-hidden="true">
          <div className="result-with-graph__bar-column d-flex flex-column align-items-center justify-content-end h-100">
            <div className="result-with-graph__bar position-relative rounded-top" style={{ height: barHeight(totalCostPublicTransportState) }}>
              <span className="result-with-graph__bar-value position-absolute fw-bold text-nowrap">{totalCostPublicTransportState} kr</span>
            </div>
          </div>
          <div className="result-with-graph__bar-column d-flex flex-column align-items-center justify-content-end h-100">
            <div className="result-with-graph__bar position-relative rounded-top" style={{ height: barHeight(variableCarCostState) }}>
              <span className="result-with-graph__bar-value position-absolute fw-bold text-nowrap">{variableCarCostState} kr</span>
            </div>
          </div>
          <div className="result-with-graph__bar-column d-flex flex-column align-items-center justify-content-end h-100">
            <div className="result-with-graph__bar result-with-graph__bar--striped position-relative rounded-top" style={{ height: barHeight(totalCarCostState) }}>
              <span className="result-with-graph__bar-value result-with-graph__bar-value--light position-absolute fw-bold text-nowrap">{totalCarCostState} kr</span>
            </div>
          </div>
        </div>
        <div id="graph-description" className="visually-hidden" aria-live="polite" role="status">
          {createAriaLiveText(totalCostPublicTransportState, variableCarCostState, totalCarCostState)}
        </div>
        <div className="d-flex" aria-hidden="true">
          <div className="result-with-graph__x-axis-label text-center">{TranslationService.get('pricecalculatorpage.result.publictransportchart')}</div>
          <div className="result-with-graph__x-axis-label text-center">{TranslationService.get('pricecalculatorpage.result.variablecarcostchart')}</div>
          <div className="result-with-graph__x-axis-label result-with-graph__light text-center">{TranslationService.get('pricecalculatorpage.result.fixedcarcostchart')}</div>
        </div>
      </div>
      <div className="container container--narrower">
        <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(cheapestAlternativeText) }}></div>
      </div>
    </div>
  );
}

export default ResultWithGraph;
